<template>
    <div style="position: fixed; z-index: 1200; bottom: 0;">
        <JackpotWidget v-if="isShowJackpotWidget" />
        <div class="miniMpTicket notranslate miniParlayShow safeAreaBottom">
            <div
                class="sm_titleBox"
                v-on:click="showMediumBetSlip"
            >
                <div class="sm_comboInfo">
                    <p class="sm_count">
                        {{ ticketCount }}
                    </p>
                    <span class="sm_comboTitle">
                        {{ i18n.t('bet_builder') }} <span class="sm_combo-odds">@{{ totalOddsDisplay }}</span>
                    </span>
                </div>
                <div class="feature-btn-container">
                    <button
                        type="button"
                        v-bind:disabled="ticketCount === 0"
                        class="sm_removeall MiniBetBuilderSlip_Btn_RemoveAll"
                        v-on:click.stop="removeBetSlip"
                    >
                        <SboIcon
                            name="system:trashOutline"
                            size="20"
                        />
                    </button>
                    <button
                        type="button"
                        class="sm_viewall MiniBetBuilderSlip_Btn_ViewAll"
                        v-bind:disabled="!isValidSubBetCount"
                        v-on:click.stop="showBigBetSlip"
                    >
                        <SboIcon
                            name="system:expand"
                            size="20"
                        />
                    </button>
                </div>
                <button
                    type="button"
                    class="btn_close MiniBetBuilderSlip_Btn_Collapse"
                    v-on:click.stop="showMiniBetSlip"
                >
                    <SboIcon
                        name="system:arrowDown"
                        size="20"
                    />
                </button>
            </div>
            <MediumBetBuilderBetSlipContent />
            <div class="miniMpContent MiniParlaySlip_view">
                <div class="ticket-maxPayout">
                    <span>{{ getMaxPayoutText }} :</span>
                    <span class="ticket-amount">
                        {{ totalPayoutDisplay }}
                    </span>
                </div>
                <Message
                    v-if="errorMessage.key"
                    status="error"
                    v-bind:messageKey="errorMessage.key"
                    class="miniMpContent-message"
                />
                <div class="ticket-inputArea">
                    <PlaceBet
                        v-bind:stake="stake"
                        v-bind:isDisableBtn="isDisablePlaceBet"
                        v-bind:balance="balance"
                        v-bind:minBet="betSlip.minBet"
                        v-bind:price="price"
                        v-bind:placeBetGa="'MiniBetBuilderSlip_Btn_PlaceBet'"
                        v-on:placeBet="placeBet"
                        v-on:changeStake="setStake"
                    />
                    <StakePad
                        v-bind:stake="stake"
                        v-bind:balance="balance"
                        v-bind:minBet="betSlip.minBet"
                        v-bind:maxBet="betSlip.maxBet"
                        v-bind:currency="customerInfo.currency"
                        v-on:changeStake="setStake"
                    />
                </div>
                <SwitchAcceptAnyOddsValue v-if="!customerInfo.isAcceptAnyOdds" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent, onUnmounted, ref } from 'vue';
    import SwitchAcceptAnyOddsValue from '@/components/SwitchAcceptAnyOddsValue.vue';
    import MediumBetBuilderBetSlipContent from '@/components/betSlip/MediumBetBuilderBetSlipContent.vue';
    import PlaceBet from '@/components/betSlip/PlaceBet.vue';
    import StakePad from '@/components/betSlip/StakePad.vue';
    import Message from '@/components/common/Message.vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import JackpotWidget from '@/components/jackpot/JackpotWidget.vue';
    import { useJackpotDisplay } from '@/libraries/composables/useJackpotDisplay';
    import { getMaxPayoutText } from '@/libraries/helpers/i18nHelper';
    import { i18n } from '@/libraries/i18n';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetBuilderBetSlipStore } from '@/stores/betSlip/betBuilderBetSlipStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';

    export default defineComponent({
        name: 'MediumBetBuilderBetSlip',
        components: {
            JackpotWidget,
            MediumBetBuilderBetSlipContent,
            SwitchAcceptAnyOddsValue,
            PlaceBet,
            StakePad,
            Message,
            SboIcon,
        },
        setup() {
            const { isShowJackpotWidget } = useJackpotDisplay();
            const { showBigBetSlip, showMiniBetSlip, showMediumBetSlip } = useBetSlipStore();
            const { customerInfo, balance } = storeToRefs(useAccountStore());

            const {
                totalOddsDisplay,
                ticketCount,
                totalPayoutDisplay,
                isValidSubBetCount,
                isUpdatingTicket,
                isPlacingBet,
                errorMessage,
                betSlip,
                stake,
                price,
                hasBettablePrice,
                isAutoRefresh,
            } = storeToRefs(useBetBuilderBetSlipStore());

            const {
                removeBetSlip,
                placeBet,
                setStake,
                refresh,
            } = useBetBuilderBetSlipStore();

            const isDisablePlaceBet = computed(() => {
                if (isPlacingBet.value) return true;
                if (isUpdatingTicket.value && !isAutoRefresh.value) return true;
                if (!isValidSubBetCount.value) return true;
                if (!hasBettablePrice.value) return true;

                if (balance.value === null) return false;
                if (balance.value < betSlip.value.minBet) return true;

                return Math.floor(balance.value) <= 0;
            });

            const ticks = ref(0);
            async function tick() {
                ticks.value++;
                const refreshOpenTicketSeconds = 15;
                if (ticks.value >= refreshOpenTicketSeconds) {
                    await refresh();
                    ticks.value = 0;
                }
            }

            window.eventEmitter.on('tick', tick);

            onUnmounted(() => {
                window.eventEmitter.off('tick', tick);
            });

            return {
                i18n,
                ticketCount,
                totalOddsDisplay,
                totalPayoutDisplay,
                isShowJackpotWidget,
                isValidSubBetCount,
                removeBetSlip,
                showBigBetSlip,
                showMiniBetSlip,
                showMediumBetSlip,
                errorMessage,
                betSlip,
                balance,
                isDisablePlaceBet,
                stake,
                placeBet,
                setStake,
                customerInfo,
                price,
                getMaxPayoutText,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/miniMpTicket";
</style>
